import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Check,
  Clear,
  Search as SearchIcon
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';
import { usePanelStyles } from '@/styles/utilities/hooks';
import { rowMatchesSearch } from '../utils/searchUtils';

/**
 * StructuresGrid Component
 * 
 * Main data grid for displaying and managing prepared PDB structures.
 * Includes search functionality, column management, and row selection.
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of structure data to display
 * @param {Array} props.columns - Column definitions for the grid
 * @param {boolean} props.loading - Whether data is loading
 * @param {Set} props.selectedIds - Set of selected row IDs
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {Function} props.onRowClick - Callback when a row is clicked
 * @param {Object} props.columnVisibilityModel - Column visibility state
 * @param {Function} props.onColumnVisibilityChange - Callback for column visibility changes
 * @param {boolean} props.isPreviewMode - Whether in preview mode
 * @param {Array} props.previewData - Preview data for changes
 * @param {Function} props.onCancelPreview - Callback to cancel preview
 * @param {Function} props.onSaveChanges - Callback to save changes
 * @param {string} props.searchTerm - Current search term
 * @param {Function} props.onSearchChange - Callback when search changes
 * @param {Array} props.searchOptions - Available search options
 */
export default function StructuresGrid({
  data,
  columns,
  loading,
  selectedIds,
  onSelectionChange,
  onRowClick,
  columnVisibilityModel,
  onColumnVisibilityChange,
  isPreviewMode,
  previewData,
  onCancelPreview,
  onSaveChanges,
  searchTerm,
  onSearchChange,
  searchOptions
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();
  
  // Apply search filter and merge preview data
  const displayData = useMemo(() => {
    let filteredData = data;
    
    // Apply search filter using centralized search logic
    if (searchTerm) {
      const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);
      filteredData = data.filter(row => rowMatchesSearch(row, searchWords));
    }
    
    // Merge preview data if in preview mode
    if (isPreviewMode && previewData && previewData.length > 0) {
      return filteredData.map(row => {
        const preview = previewData.find(p => p.id === row.id);
        if (preview) {
          // Merge preview data with original row (preview values override)
          return { ...row, ...preview };
        }
        return row;
      });
    }
    
    return filteredData;
  }, [data, searchTerm, isPreviewMode, previewData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Bar */}
      <Box sx={{ 
        ...panelStyles.header,
        cursor: 'default',  // Not draggable, so override cursor
        gap: theme.spacing(1.5),  // Minimum gap between components (matches Upload panel)
        overflowX: 'auto',
        overflowY: 'hidden',
        '&::-webkit-scrollbar': {
          height: theme.spacing(0.5),
          backgroundColor: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          borderRadius: theme.spacing(0.25)
        }
      }}>
        <Typography sx={{
          ...panelStyles.headerTitle,
          whiteSpace: 'nowrap',
          minWidth: 'fit-content'
        }}>
          Prepared PDB Files ({displayData.length})
        </Typography>
        
        {/* Search Bar */}
        <Autocomplete
          freeSolo
          size="small"
          options={searchOptions || []}
          value={searchTerm}
          onInputChange={(event, newValue) => onSearchChange(newValue)}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <li key={key} {...otherProps}>
                {option}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search structures..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Close the autocomplete dropdown by blurring the input
                  e.target.blur();
                  // Refocus after a short delay to keep the field accessible
                  setTimeout(() => e.target.focus(), 50);
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: theme.dimensions.icon.small }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                sx: {
                  backgroundColor: 'white',
                  height: theme.dimensions.button.small, // Match button height (24px)
                  '& input': {
                    padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
                    fontSize: theme.typography.fontSize.sm,
                  }
                }
              }}
            />
          )}
          sx={{
            minWidth: theme.spacing(25),
            width: theme.spacing(35),
          }}
        />
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Clear />}
            onClick={onCancelPreview}
            disabled={!isPreviewMode || previewData?.length === 0}
            sx={{ 
              minWidth: theme.spacing(7.5)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Check />}
            onClick={onSaveChanges}
            disabled={!isPreviewMode || previewData?.length === 0}
            sx={{ 
              minWidth: theme.spacing(7.5)
            }}
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <DataGridPro
          rows={displayData}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          rowHeight={theme.dimensions.dataGrid.rowHeight}
          columnHeaderHeight={theme.dimensions.dataGrid.headerHeight}
          hideFooter
          rowSelectionModel={{ ids: selectedIds, type: 'include' }}
          onRowSelectionModelChange={onSelectionChange}
          onRowClick={(params) => {
            if (onRowClick) {
              onRowClick(params);
            }
          }}
          autoHeight={false}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityChange}
          sx={{
            border: 'none',
            '& .MuiDataGrid-root': {
              fontSize: theme.typography.fontSize.sm,
            },
            '& .MuiDataGrid-cell': {
              fontSize: theme.typography.fontSize.sm,
              padding: theme.spacing(0, 1),
            },
            '& .MuiDataGrid-columnHeaders': {
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
              minHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
              maxHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: theme.typography.fontSize.sm,
              padding: theme.spacing(0, 1),
              minHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
              maxHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
            },
            '& .MuiDataGrid-row': {
              minHeight: `${theme.dimensions.dataGrid.rowHeight}px !important`,
              maxHeight: `${theme.dimensions.dataGrid.rowHeight}px !important`,
            },
            '& .MuiDataGrid-cell--textLeft': {
              justifyContent: 'flex-start',
            },
            '& .MuiDataGrid-cell--textRight': {
              justifyContent: 'flex-end',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              minHeight: theme.spacing(12.5),
              paddingTop: '0 !important'
            }
          }}
        />
      </Box>
    </Box>
  );
}